import { AppConfig } from './app-config.model';

export const DEFAULT_APP_CONFIG: AppConfig = {
  appInfo: {
    title: 'Kfh Dashboard',
    description: 'Minimalist investment fund & portfolio management platform',
  },
  branding: {
    logos: {
      primary: 'assets/image/skfh-logo.png',
      primarySvg: 'assets/image/skfh-logo.png',
      loginSideImage: 'assets/image/page-side-img.jpg',
    },
    favicon: 'assets/image/miyar-logo.png',
    defaultAvatar: 'assets/image/default.jpg',
  },
  theme: {
    baseTokens: {
      spacingUnit: '4px',
      borderRadius: '20px',
      fontBaseSize: '14px',
      controlHeightBase: '40px',
    },
    typography: {
      fontFamilyPrimary: "'Inter', 'Tajawal', sans-serif",
      googleFontsUrl:
        'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Tajawal:wght@400;500;700&display=swap',
      scale: {
        displayLarge: { size: '32px', weight: '600' },
        headlineLarge: { size: '20px' },
        headlineMedium: { size: '18px' },
        titleLarge: { size: '16px' },
        titleMedium: { size: '16px' },
        bodyLarge: { size: '14px', weight: '400' },
        bodyMedium: { size: '14px' },
        bodySmall: { size: '14px' },
        caption: { size: '14px' },
      },
    },
    spacing: {
      '1': 'calc(var(--sys-base-tokens-spacing-unit) * 1)',
      '2': 'calc(var(--sys-base-tokens-spacing-unit) * 2)',
      '3': 'calc(var(--sys-base-tokens-spacing-unit) * 3)',
      '4': 'calc(var(--sys-base-tokens-spacing-unit) * 4)',
      '5': 'calc(var(--sys-base-tokens-spacing-unit) * 5)',
      '6': 'calc(var(--sys-base-tokens-spacing-unit) * 6)',
      '8': 'calc(var(--sys-base-tokens-spacing-unit) * 8)',
    },
    sizing: {
      iconSm: 'calc(var(--sys-base-tokens-spacing-unit) * 4)',
      iconMd: 'calc(var(--sys-base-tokens-spacing-unit) * 6)',
      iconLg: 'calc(var(--sys-base-tokens-spacing-unit) * 8)',
      avatarSm: 'calc(var(--sys-base-tokens-spacing-unit) * 8)',
      avatarMd: 'calc(var(--sys-base-tokens-spacing-unit) * 10)',
      avatarLg: 'calc(var(--sys-base-tokens-spacing-unit) * 12)',
      buttonSm: 'calc(var(--sys-base-tokens-control-height-base) * 0.8)',
      buttonMd: 'calc(var(--sys-base-tokens-control-height-base) * 1.0)',
      buttonLg: 'calc(var(--sys-base-tokens-control-height-base) * 1.2)',
    },
    colors: {
      primary: '#2F743F',
      primaryContrast: '#FFFFFF',
      blackPrimary: '#175331',
      primaryDark: '#176D3A',
      primaryRgb: '47, 116, 63',
      primaryContainer: '#F5FFF3',
      secondaryLight: '#F5F8F8',
      secondaryBase: '#DDECDC',
      lightGray: '#CFD6DB',
      darkGray: '#53555A',
      accent: '#176D3A',
      warn: '#C89704',
      background: '#F5F8F8',
      surface: '#FFFFFF',
      surfaceVariant: '#F5F8F8',
      textPrimary: '#000000',
      textSecondary: '#53555A',
      textMuted: '#53555A',
      textDisabled: '#53555A',
      disabledBg: '#CFD6DB',
      disabledCheckboxBg: '#F5F8F8',
      bgOverlay: '#191B2180',
      highlight: '#00000000',
      highlightHover: '#0000000D',
      highlightPressed: '#00000012',
      bgBrandHover: '#175331',
      bgBrandSecondary: '#DDECDC',
      textHeading: '#000000',
      textPlaceholder: 'var(--sys-colors-text-muted)',
      textLink: '#176D3A',
      iconPrimary: '#176D3A',
      iconSecondary: '#53555A',
      iconOnDark: '#FFFFFF',
      borderLight: '#CFD6DB',
      borderMedium: '#CFD6DB',
      borderStripe: '#F5F8F8',
      borderNavbar: 'var(--sys-colors-surface-variant)',
      status: {
        successBg: '#DCFCE7',
        successBorder: 'var(--sys-colors-primary)',
        successText: '#065F46',
        warningBg: '#FEF9C3',
        warningBorder: '#EAB308',
        errorBg: '#EE2C24',
        errorBorder: '#EE2C24',
        errorAccent: '#EE2C24',
      },
      button: {
        primaryBg: 'var(--sys-colors-primary)',
        primaryText: 'var(--sys-colors-surface)',
        cancelBg: 'var(--sys-colors-secondary-light)',
        cancelText: 'var(--sys-colors-text-secondary)',
        cancelHoverBg: 'var(--sys-colors-surface-variant)',
        dangerBg: 'var(--sys-colors-status-error-bg)',
        dangerText: 'var(--sys-colors-status-error-accent)',
        rejectBg: 'var(--sys-colors-status-error-accent)',
        rejectText: 'var(--sys-colors-surface)',
        defaultBg: 'var(--sys-colors-surface)',
        addInvestorBg: 'var(--sys-colors-primary)',
        addInvestorText: '#FFFFFF',
      },
      chart: {
        green: 'var(--sys-colors-primary)',
        red: 'var(--sys-colors-warn)',
        active: 'var(--sys-colors-primary)',
      },
      misc: {
        scrollbarThumb: 'var(--sys-colors-text-muted)',
      },
    },
    shape: {
      borderRadius: {
        none: '0',
        xs: '2px',
        sm: '4px',
        md: '8px',
        lg: '16px',
        xl: '8px',
        xxl: '16px',
        xxxl: '24px',
        pill: '9999px',
      },
    },
    elevation: {
      level1: '0 1px 3px -1px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.05)',
      level2: '0 2px 8px -2px rgba(0, 0, 0, 0.15)',
      levelInset: 'inset 0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    },
    motion: {
      durationFast: '100ms',
      durationNormal: '150ms',
      durationMedium: '250ms',
      durationSlow: '350ms',
      easingStandard: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
    },
    layout: {
      sidebarCollapsedWidth: 'calc(var(--sys-base-tokens-control-height-base) * 1.95)',
      navbarHeight: 'calc(var(--sys-base-tokens-control-height-base) * 1.75)',
    },
    components: {
      navbar: {
        background: 'var(--sys-colors-surface)',
      },
      sidebar: {
        background: 'var(--sys-colors-surface)',
      },
      toaster: {
        borderRadius: 'var(--sys-shape-border-radius-lg)',
        positionBottom: 'calc(var(--sys-base-tokens-spacing-unit) * 6)',
        positionLeftPercent: '50%',
      },
      table: {
        headerBg: 'var(--sys-colors-secondary-base)',
        headerTextColor: 'var(--sys-colors-text-heading)',
        headerFontWeight: 'var(--sys-typography-scale-display-large-weight)',
        stripeBg: 'var(--sys-colors-secondary-light)',
        borderColor: 'var(--sys-colors-secondary-light)',
        textColor: 'var(--sys-colors-text-primary)',
        fontWeight: 'var(--sys-typography-scale-body-large-weight)',
      },
      input: {
        borderRadius: 'var(--sys-shape-border-radius-lg)',
        borderWidth: '1px',
        sizes: {
          large: {
            height: 'var(--sys-sizing-button-lg)',
            padding: '0 var(--sys-spacing-4)',
            fontSize: 'var(--sys-typography-scale-body-large-size)',
          },
        },
        variants: {
          default: {
            bg: 'var(--sys-colors-surface)',
            border: 'var(--sys-colors-border-medium)',
            text: 'var(--sys-colors-text-primary)',
            placeholder: 'var(--sys-colors-text-placeholder)',
            focusBorder: 'var(--sys-colors-primary)',
            focusShadow: '0 0 0 2px rgba(47, 116, 63, 0.2)',
          },
        },
      },
      button: {
        borderRadius: 'var(--sys-shape-border-radius-lg)',
        sizes: {
          large: {
            height: 'var(--sys-sizing-button-lg)',
            minWidth: 'calc(var(--sys-base-tokens-control-height-base) * 3)',
            padding: '0 var(--sys-spacing-6)',
            fontSize: 'var(--sys-typography-scale-body-large-size)',
          },
          medium: {
            height: 'var(--sys-sizing-button-md)',
            minWidth: 'calc(var(--sys-base-tokens-control-height-base) * 2.5)',
            padding: '0 var(--sys-spacing-4)',
            fontSize: 'var(--sys-typography-scale-body-large-size)',
          },
          small: {
            height: 'var(--sys-sizing-button-sm)',
            minWidth: 'calc(var(--sys-base-tokens-control-height-base) * 2)',
            padding: '0 var(--sys-spacing-3)',
            fontSize: 'var(--sys-typography-scale-body-medium-size)',
          },
        },
        variants: {
          primary: {
            bg: 'var(--sys-colors-button-primary-bg)',
            text: 'var(--sys-colors-button-primary-text)',
            hoverBg: 'var(--sys-colors-bg-brand-hover)',
            hoverText: 'var(--sys-colors-button-primary-text)',
          },
          cancel: {
            bg: 'var(--sys-colors-button-cancel-bg)',
            text: 'var(--sys-colors-button-cancel-text)',
            border: '1px solid var(--sys-colors-border-medium)',
            hoverBg: 'var(--sys-colors-button-cancel-hover-bg)',
            hoverText: 'var(--sys-colors-text-heading)',
          },
          default: {
            bg: 'var(--sys-colors-button-default-bg)',
            text: 'var(--sys-colors-primary)',
            border: '1px solid var(--sys-colors-primary)',
            hoverBg: 'var(--sys-colors-border-light)',
            hoverText: 'var(--sys-colors-primary)',
          },
          success: {
            bg: 'var(--sys-colors-status-success-bg)',
            text: 'var(--sys-colors-status-success-text)',
            hoverBg: 'var(--sys-colors-status-success-border)',
            hoverText: 'var(--sys-colors-status-success-text)',
          },
          danger: {
            bg: 'var(--sys-colors-button-danger-bg)',
            text: 'var(--sys-colors-button-danger-text)',
            hoverBg: 'var(--sys-colors-button-reject-bg)',
            hoverText: 'var(--sys-colors-button-reject-text)',
          },
          addInvestor: {
            bg: 'var(--sys-colors-button-add-investor-bg)',
            text: 'var(--sys-colors-button-add-investor-text)',
            borderRadius: 'calc(var(--sys-base-tokens-border-radius) * 0.4)',
            hoverBg: 'var(--sys-colors-button-add-investor-bg)',
            hoverText: 'var(--sys-colors-button-add-investor-text)',
          },
        },
      },
    },
  },
};
